import React, { useEffect, useState } from "react";
import FanAvatar from "../components/FanAvatar";
import {
  BENEFIT_FILL as FILL,
  BENEFIT_GREEN as GREEN,
  type IgConnectView,
  connectView,
  doneView,
  expiredView,
  igConnectStage,
  waitingView,
} from "../components/IgConnectSteps";
import { useAndroidBack } from "../hooks/useAndroidBack";
import { useInstagramDmVerify } from "../hooks/useInstagramDmVerify";
import { t } from "../i18n";
import "./FanConnectFlow.css";

// App fan mode sign-up with "Connect Instagram" (founder's own screens,
// polished over rounds 1–7, 2026-10-27/28; client/.claude/
// instagram-connect-fan-flow-r7.html). FanNamePage shows this instead of its
// own screen only while the server has "Verify with a DM" on for this fan
// (GET /fans/me igDmVerify); otherwise today's name screen, unchanged.
//
//   name ─Continue→ connect ─Connect Instagram→ waiting ─the DM arrives→ done ─→ /fan
//                     │ Skip for now ─→ /fan        │ Cancel → connect
//                                                    ╰ 30 minutes → expired ─Try again→ waiting
//                                                                       ╰ Skip for now → /fan
//
// The stage and the four Instagram steps are components/IgConnectSteps (the
// talent's "Verify with a DM" backup shows the same screens). The name step's
// photo bubble and field come from FanNamePage (it owns the photo upload and
// the nickname).

type Step = "name" | "connect" | "waiting" | "done" | "expired";

type Props = {
  /** The photo bubble (tap to change) + nickname field, rendered by FanNamePage. */
  nameFields: React.ReactNode;
  /** The nickname as typed, for the "you" bubble on the connect step. */
  displayName: string;
  avatarUrl: string | null;
  /** Step 1's Continue: FanNamePage saves the nickname. */
  onNameDone: () => void;
  /** Step 1's Back: leave fan sign-up. */
  onLeave: () => void;
  /** Instagram vouched for this handle (FanNamePage mirrors it on the device). */
  onConnected: (username: string) => void;
  /** Done or skipped: into the app. */
  onFinish: () => void;
};

export default function FanConnectFlow({ nameFields, displayName, avatarUrl, onNameDone, onLeave, onConnected, onFinish }: Props) {
  const [step, setStep] = useState<Step>("name");
  const [username, setUsername] = useState("");
  const v = useInstagramDmVerify((name) => {
    setUsername(name);
    onConnected(name);
    setStep("done");
  });

  // The round trip drives the screen: a link in hand → waiting; 30 minutes
  // without the DM → expired. (A failed start stays on connect, with a line.)
  useEffect(() => {
    if (v.phase === "waiting") setStep("waiting");
    // ("taken" is talent-only; were a fan ever to get it, the same ending.)
    else if (v.phase === "expired" || v.phase === "taken") setStep("expired");
  }, [v.phase]);

  const cancel = () => {
    v.cancel();
    setStep("connect");
  };

  // The Android back button does what each step's own back does: Back on the
  // name and Connect steps, Cancel on the wait, Back on "too long", and into
  // the app once connected.
  useAndroidBack(() => {
    if (step === "name") onLeave();
    else if (step === "connect") setStep("name");
    else if (step === "waiting") cancel();
    else if (step === "expired") setStep("connect");
    else onFinish();
  });

  let view: IgConnectView;
  if (step === "name") {
    view = {
      back: onLeave,
      content: <>{nameFields}</>,
      footer: (
        <button
          type="button"
          className="fcf-go"
          onClick={() => {
            onNameDone();
            setStep("connect");
          }}
        >
          {t("fanConnect.continue")}
        </button>
      ),
    };
  } else if (step === "connect") {
    view = connectView({
      you: <FanAvatar className="fcf-you" name={displayName} avatarUrl={avatarUrl} silhouetteSize={40} />,
      benefits: [
        {
          text: t("fanConnect.benefitLeaderboard"),
          icon: (
            <>
              <path d="m3.5 8 3.6 3 4.9-5.5L16.9 11l3.6-3v9.5H3.5Z" fill={FILL} stroke={GREEN} strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M3.5 20.5h17" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" />
            </>
          ),
        },
        {
          text: t("fanConnect.benefitDjs"),
          icon: (
            <>
              <path d="M4 15v-3a8 8 0 0 1 16 0v3" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" />
              <rect x="3" y="13.5" width="4.6" height="7" rx="2" fill={FILL} stroke={GREEN} strokeWidth="1.8" />
              <rect x="16.4" y="13.5" width="4.6" height="7" rx="2" fill={FILL} stroke={GREEN} strokeWidth="1.8" />
            </>
          ),
        },
        {
          text: t("fanConnect.benefitSafe"),
          icon: (
            <>
              <path d="M12 3.2 19 6v5.6c0 4.3-3 7.6-7 9.2-4-1.6-7-4.9-7-9.2V6Z" fill={FILL} stroke={GREEN} strokeWidth="1.8" strokeLinejoin="round" />
              <path d="m8.8 12.1 2.2 2.2 4.3-4.4" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </>
          ),
        },
      ],
      v,
      onBack: () => setStep("name"),
      onSkip: onFinish,
    });
  } else if (step === "waiting") {
    view = waitingView({ v, onCancel: cancel });
  } else if (step === "done") {
    view = doneView({ username, continueLabel: t("fanConnect.doneContinue"), onContinue: onFinish });
  } else {
    view = expiredView({ v, onBack: () => setStep("connect"), onSkip: onFinish });
  }

  return igConnectStage(step, view);
}
